// ============================================================================
// SHABZ CLOSET — Central Theme Engine (presentation only)
// One place that owns all theme tokens, presets, colour conversion, contrast
// checks and the apply/persist logic. Components consume CSS variables; nothing
// here touches business logic, billing, inventory or API business data.
// ============================================================================

export const LS_KEY = 'sc_theme_v1';
export const LS_BRANDING = 'sc_branding_v1';
export const LS_SPLASH = 'sc_splash_v1';
export const LS_LOGIN = 'sc_login_v1';

// ---- The owner-editable colours (canonical = HSL triple "H S% L%") ---------
// Everything else (foregrounds, ring, muted) is DERIVED for readability.
export const COLOR_FIELDS = [
  { key: 'primary', label: 'Primary / brand' },
  { key: 'accent', label: 'Secondary / accent' },
  { key: 'background', label: 'Background' },
  { key: 'sidebar', label: 'Sidebar' },
  { key: 'header', label: 'Header' },
  { key: 'card', label: 'Card / panel' },
  { key: 'foreground', label: 'Text colour' },
  { key: 'mutedForeground', label: 'Muted text' },
  { key: 'button', label: 'Button colour' },
  { key: 'buttonForeground', label: 'Button text' },
  { key: 'border', label: 'Border colour' },
  { key: 'success', label: 'Success' },
  { key: 'warning', label: 'Warning' },
  { key: 'destructive', label: 'Error' },
];

// ---- Default UI (non-colour) options ---------------------------------------
export const DEFAULT_UI = {
  cardStyle: 'rounded',        // standard | rounded | sharp
  buttonStyle: 'rounded',      // standard | rounded | pill
  density: 'comfortable',      // comfortable | compact
  borderIntensity: 'normal',   // subtle | normal
  shadows: true,               // subtle shadows on/off
  sidebar: 'normal',           // compact | normal
};

export const DEFAULT_BRANDING = {
  logo: '',                    // base64 data URL (optional)
  logoSize: 'medium',          // small | medium | large
  showInSidebar: true,
  showOnInvoice: false,
  businessName: 'SHABZ CLOSET',
};

// Login screen cover (background). "default" keeps the existing themed backdrop.
export const DEFAULT_LOGIN = {
  bgType: 'default',           // default | solid | gradient | image
  bgColor: '#131316',
  gradFrom: '#131316', gradTo: '#26241E', gradDir: 160,
  bgImage: '',
  overlay: false, overlayOpacity: 0.5,
};

// Fully-editable splash / opening screen configuration.
export const DEFAULT_SPLASH = {
  enabled: true,
  logoSize: 96,                // px
  logoAlign: 'center',         // left | center | right
  logoAnim: 'none',            // none | pulse | reveal
  showName: true, name: 'SHABZ CLOSET', nameSize: 30, nameWeight: 700, nameSpacing: 0.5, nameColor: '#F4F1EB',
  showTagline: true, tagline: 'Billing & Store Operations', tagSize: 14, tagWeight: 400, tagSpacing: 1.5, tagColor: '#A6A196',
  bgType: 'gradient',          // solid | gradient | image
  bgColor: '#131316',
  gradFrom: '#17161A', gradTo: '#2A2718', gradDir: 160,
  bgImage: '',
  overlay: false, overlayColor: '#000000', overlayOpacity: 0.4,
  accent: '#C9A24B',
  animStyle: 'elegant',        // fade | fade-scale | slide-up | slide-down | zoom | logo-reveal | elegant | none
  animSpeed: 1.4,              // seconds (0.5 - 3)
  transition: 'fade',          // fade | slide | zoom | none
};

// Build a CSS background style object from a splash/login-style config.
export function buildBgStyle(cfg, fallbackToTheme) {
  if (!cfg) return {};
  const t = cfg.bgType;
  if (t === 'image' && cfg.bgImage) return { backgroundImage: `url(${cfg.bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' };
  if (t === 'gradient') return { backgroundImage: `linear-gradient(${cfg.gradDir || 160}deg, ${cfg.gradFrom}, ${cfg.gradTo})` };
  if (t === 'solid') return { backgroundColor: cfg.bgColor };
  return fallbackToTheme ? {} : { backgroundColor: cfg.bgColor };
}

// ---------------------------------------------------------------------------
// PRESETS — every preset defines the base colours. Foregrounds derived.
// ---------------------------------------------------------------------------
const P = (o) => o; // identity: presets are explicit token objects

export const PRESETS = [
  { id: 'shabz_gold', name: 'SHABZ Gold', desc: 'Charcoal + rich gold',
    tokens: P({ background: '240 6% 8%', foreground: '40 30% 94%', card: '240 7% 11%', header: '240 8% 9%', mutedForeground: '40 8% 62%', primary: '42 62% 55%', accent: '240 6% 16%', sidebar: '240 10% 5%', button: '42 62% 55%', border: '240 5% 20%', success: '150 45% 45%', warning: '40 72% 52%', destructive: '5 65% 52%', buttonForeground: '240 8% 8%' }) },
  { id: 'black_gold', name: 'Black & Gold', desc: 'Pure black + gold',
    tokens: P({ background: '0 0% 4%', foreground: '45 30% 95%', card: '0 0% 8%', header: '0 0% 5%', mutedForeground: '45 6% 60%', primary: '45 75% 55%', accent: '45 20% 14%', sidebar: '0 0% 2%', button: '45 75% 55%', border: '0 0% 16%', success: '150 45% 45%', warning: '42 80% 52%', destructive: '5 70% 54%', buttonForeground: '0 0% 6%' }) },
  { id: 'burgundy_gold', name: 'Burgundy & Gold', desc: 'Burgundy + gold',
    tokens: P({ background: '345 30% 10%', foreground: '40 30% 94%', card: '345 25% 14%', header: '345 32% 9%', mutedForeground: '345 10% 66%', primary: '42 62% 55%', accent: '345 28% 20%', sidebar: '345 38% 7%', button: '42 62% 55%', border: '345 20% 24%', success: '150 45% 45%', warning: '40 72% 52%', destructive: '5 72% 56%', buttonForeground: '345 30% 10%' }) },
  { id: 'navy_gold', name: 'Navy & Gold', desc: 'Navy + gold',
    tokens: P({ background: '222 40% 11%', foreground: '40 30% 94%', card: '222 34% 15%', header: '222 44% 9%', mutedForeground: '215 15% 68%', primary: '42 64% 56%', accent: '222 32% 22%', sidebar: '222 48% 8%', button: '42 64% 56%', border: '222 24% 24%', success: '150 45% 46%', warning: '40 72% 52%', destructive: '5 70% 56%', buttonForeground: '222 40% 10%' }) },
  { id: 'emerald', name: 'Emerald', desc: 'Deep green + light gold',
    tokens: P({ background: '160 30% 8%', foreground: '45 28% 93%', card: '160 24% 11%', header: '160 32% 7%', mutedForeground: '160 10% 62%', primary: '45 66% 60%', accent: '160 28% 17%', sidebar: '160 34% 6%', button: '45 66% 60%', border: '160 18% 20%', success: '150 50% 46%', warning: '40 72% 52%', destructive: '5 66% 54%', buttonForeground: '160 30% 9%' }) },
  { id: 'royal_blue', name: 'Royal Blue', desc: 'Navy + bright blue',
    tokens: P({ background: '222 45% 11%', foreground: '210 30% 96%', card: '222 38% 15%', header: '222 50% 9%', mutedForeground: '215 15% 68%', primary: '213 90% 60%', accent: '222 36% 22%', sidebar: '222 50% 8%', button: '213 90% 60%', border: '222 24% 24%', success: '150 45% 46%', warning: '40 72% 52%', destructive: '5 70% 58%', buttonForeground: '222 45% 10%' }) },
  { id: 'light_minimal', name: 'Light Minimal', desc: 'White + charcoal',
    tokens: P({ background: '0 0% 100%', foreground: '220 16% 14%', card: '0 0% 100%', header: '0 0% 100%', mutedForeground: '220 9% 45%', primary: '220 16% 20%', accent: '220 14% 96%', sidebar: '220 18% 15%', button: '220 16% 20%', border: '220 13% 88%', success: '150 55% 38%', warning: '38 80% 46%', destructive: '4 74% 48%', buttonForeground: '0 0% 100%' }) },
  { id: 'dark_luxury', name: 'Dark Luxury', desc: 'Plum charcoal + champagne',
    tokens: P({ background: '270 15% 9%', foreground: '40 25% 93%', card: '270 14% 13%', header: '270 18% 8%', mutedForeground: '270 8% 64%', primary: '40 55% 62%', accent: '270 14% 19%', sidebar: '270 20% 6%', button: '40 55% 62%', border: '270 10% 22%', success: '150 45% 46%', warning: '40 72% 54%', destructive: '5 66% 56%', buttonForeground: '270 15% 9%' }) },
];

// The SHABZ brand default + the "Reset to default" target.
export const DEFAULT_PRESET_ID = 'shabz_gold';

export function presetById(id) {
  return PRESETS.find((p) => p.id === id) || PRESETS[0];
}

export function defaultThemeConfig() {
  const p = presetById(DEFAULT_PRESET_ID);
  return {
    name: p.name,
    preset: p.id,
    tokens: { ...p.tokens },
    ui: { ...DEFAULT_UI },
  };
}

// ---------------------------------------------------------------------------
// Colour conversion helpers  (HEX <-> "H S% L%")
// ---------------------------------------------------------------------------
export function hexToHslTriple(hex) {
  let h = String(hex || '').replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, sat = 0; const lum = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = lum > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hue = (b - r) / d + 2; break;
      default: hue = (r - g) / d + 4;
    }
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(lum * 100)}%`;
}

export function hslTripleToHex(triple) {
  const m = String(triple || '').match(/([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/);
  if (!m) return '#000000';
  let h = parseFloat(m[1]) / 360, s = parseFloat(m[2]) / 100, l = parseFloat(m[3]) / 100;
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3); g = hue2rgb(p, q, h); b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Relative luminance (WCAG) from an HSL triple.
function luminance(triple) {
  const hex = hslTripleToHex(triple).replace('#', '');
  const rgb = [0, 2, 4].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

// WCAG contrast ratio between two HSL triples (1..21).
export function contrastRatio(a, b) {
  const l1 = luminance(a), l2 = luminance(b);
  const hi = Math.max(l1, l2), lo = Math.min(l1, l2);
  return Math.round(((hi + 0.05) / (lo + 0.05)) * 100) / 100;
}

// Auto pick readable foreground (near-white or near-black) for a surface colour.
function autoForeground(triple) {
  return luminance(triple) > 0.45 ? '222 15% 12%' : '0 0% 100%';
}

// Mix a colour toward another (0..1) — used for "subtle" borders.
function mixTriple(a, b, t) {
  const pa = String(a).match(/([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/);
  const pb = String(b).match(/([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/);
  if (!pa || !pb) return a;
  const h = +pa[1] + (+pb[1] - +pa[1]) * t;
  const s = +pa[2] + (+pb[2] - +pa[2]) * t;
  const l = +pa[3] + (+pb[3] - +pa[3]) * t;
  return `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`;
}

// ---------------------------------------------------------------------------
// APPLY — write every CSS variable + root data-attributes. Safe & idempotent.
// ---------------------------------------------------------------------------
const RADIUS = { sharp: '0rem', standard: '0.5rem', rounded: '1rem' };
const BTN_RADIUS = { standard: '0.5rem', rounded: '0.75rem', pill: '9999px' };

export function applyThemeConfig(cfg) {
  if (!cfg || !cfg.tokens || typeof document === 'undefined') return;
  const t = cfg.tokens;
  const ui = { ...DEFAULT_UI, ...(cfg.ui || {}) };
  const root = document.documentElement;
  const set = (k, v) => v != null && root.style.setProperty(k, v);

  const fg = t.foreground;
  set('--background', t.background);
  set('--foreground', fg);
  set('--card', t.card);
  set('--card-foreground', fg);
  set('--popover', t.card);
  set('--popover-foreground', fg);
  set('--header', t.header || t.card);
  set('--header-foreground', fg);
  set('--primary', t.primary);
  set('--primary-foreground', autoForeground(t.primary));
  set('--ring', t.primary);
  set('--chart-1', t.primary);
  set('--secondary', t.accent);
  set('--secondary-foreground', fg);
  set('--accent', t.accent);
  set('--accent-foreground', fg);
  set('--muted', t.accent);
  set('--muted-foreground', t.mutedForeground);
  set('--input', t.border);
  set('--border', ui.borderIntensity === 'subtle' ? mixTriple(t.border, t.background, 0.5) : t.border);
  set('--sidebar', t.sidebar);
  set('--sidebar-foreground', autoForeground(t.sidebar));
  set('--sidebar-muted', mixTriple(autoForeground(t.sidebar), t.sidebar, 0.45));
  set('--button', t.button);
  set('--button-foreground', t.buttonForeground || autoForeground(t.button));
  set('--success', t.success);
  set('--success-foreground', autoForeground(t.success));
  set('--warning', t.warning);
  set('--warning-foreground', autoForeground(t.warning));
  set('--destructive', t.destructive);
  set('--destructive-foreground', autoForeground(t.destructive));

  // Shape / density tokens
  set('--radius', RADIUS[ui.cardStyle] || RADIUS.rounded);
  set('--btn-radius', BTN_RADIUS[ui.buttonStyle] || BTN_RADIUS.rounded);
  set('--sidebar-width', ui.sidebar === 'compact' ? '4.75rem' : '15rem');
  root.setAttribute('data-density', ui.density || 'comfortable');
  root.setAttribute('data-shadows', ui.shadows ? 'on' : 'off');
  root.setAttribute('data-sidebar', ui.sidebar || 'normal');
}

// ---------------------------------------------------------------------------
// Persistence cache (localStorage) — theme config ONLY. Never secrets/data.
// ---------------------------------------------------------------------------
export function cacheTheme(cfg) {
  try { localStorage.setItem(LS_KEY, JSON.stringify({ tokens: cfg.tokens, ui: cfg.ui, preset: cfg.preset, name: cfg.name })); } catch (e) {}
}
export function readCachedTheme() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (c && c.tokens) return c;
  } catch (e) {}
  return null;
}

// Branding cache (logo etc.) — public presentation data. Lets the Login screen
// (which loads before auth) show the company logo. Never stores secrets.
export function cacheBranding(branding) {
  try { localStorage.setItem(LS_BRANDING, JSON.stringify(branding || {})); } catch (e) {}
}
export function readCachedBranding() {
  try {
    const raw = localStorage.getItem(LS_BRANDING);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {}
  return null;
}

export function cacheSplash(s) { try { localStorage.setItem(LS_SPLASH, JSON.stringify(s || {})); } catch (e) {} }
export function readCachedSplash() {
  try { const raw = localStorage.getItem(LS_SPLASH); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
}
export function cacheLogin(l) { try { localStorage.setItem(LS_LOGIN, JSON.stringify(l || {})); } catch (e) {} }
export function readCachedLogin() {
  try { const raw = localStorage.getItem(LS_LOGIN); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
}

// Set browser tab title + favicon from branding (presentation only).
export function applyBrandMeta(branding) {
  if (typeof document === 'undefined' || !branding) return;
  const name = (branding.businessName || '').trim() || 'SHABZ CLOSET';
  document.title = name;
  if (branding.logo) {
    let link = document.querySelector("link[rel='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = branding.logo;
  }
}

// Normalise a settings.theme object (may be legacy {primary,name}) into a full
// config. Legacy accent-only themes keep the current light surfaces.
export function normaliseTheme(themeFromSettings) {
  if (!themeFromSettings) return null;
  if (themeFromSettings.tokens) {
    return {
      name: themeFromSettings.name || 'Custom',
      preset: themeFromSettings.preset || 'custom',
      tokens: themeFromSettings.tokens,
      ui: { ...DEFAULT_UI, ...(themeFromSettings.ui || {}) },
    };
  }
  return null; // legacy accent-only handled by applyTheme(primary) fallback
}

// Apply cached theme immediately at module load to avoid a flash-of-default.
export function bootTheme() {
  const cached = readCachedTheme();
  if (cached) applyThemeConfig(cached);
  applyMode(readMode());
  const b = readCachedBranding();
  if (b) applyBrandMeta(b);
}

// ---------------------------------------------------------------------------
// Per-device Light/Dark mode overlay. The owner's saved theme is the "dark"
// look; choosing Light swaps to the built-in light_minimal surfaces. Stored
// per device (localStorage), never on the server, so it doesn't fight the
// shared theme engine.
// ---------------------------------------------------------------------------
export const LS_MODE = 'sc_mode';
export function readMode() {
  try { return localStorage.getItem(LS_MODE) || 'dark'; } catch (e) { return 'dark'; }
}
export function saveMode(m) { try { localStorage.setItem(LS_MODE, m); } catch (e) {} }
export function applyMode(mode) {
  if (typeof document === 'undefined') return;
  const base = readCachedTheme() || defaultThemeConfig();
  if (mode === 'light') {
    applyThemeConfig({ tokens: presetById('light_minimal').tokens, ui: base.ui, preset: 'light_minimal', name: 'Light' });
    document.documentElement.classList.remove('dark');
  } else {
    applyThemeConfig(base);
    document.documentElement.classList.add('dark');
  }
}

